import { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronDown, Check, X } from "lucide-react";

/**
 * Searchable dropdown — drop-in replacement for a controlled <select> when the
 * option list gets long (customers, products, farmers, users).
 *
 * Props:
 *   value             — currently selected option's value (string)
 *   onChange(value)   — callback with the new value (or "" when cleared)
 *   options           — [{ value, label, sublabel?, keywords? }]
 *   placeholder       — shown when nothing selected
 *   required          — form validation
 *   disabled
 *   allowClear        — show an "x" to clear the selection (default true)
 *   testId            — root data-testid; option items get `<testId>-opt-<value>`
 *   className
 *
 * Filtering matches against label + sublabel + any extra keywords, case-insensitive.
 */
export function SearchSelect({
  value = "",
  onChange,
  options = [],
  placeholder = "Select…",
  required = false,
  disabled = false,
  allowClear = true,
  testId,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)) || null,
    [options, value],
  );

  const filtered = useMemo(() => {
    if (!q.trim()) return options;
    const needle = q.trim().toLowerCase();
    return options.filter((o) => {
      const hay = [o.label, o.sublabel, ...(o.keywords || [])]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [options, q]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Focus search on open
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const pick = (val) => {
    onChange?.(val);
    setOpen(false);
    setQ("");
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange?.("");
  };

  return (
    <div ref={rootRef} className={`relative ${className}`} data-testid={testId}>
      {/* Hidden native input for HTML form `required` support */}
      {required && (
        <input
          tabIndex={-1}
          aria-hidden
          className="sr-only opacity-0 absolute pointer-events-none"
          value={value || ""}
          required
          onChange={() => {}}
          onFocus={() => setOpen(true)}
        />
      )}

      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`w-full flex items-center justify-between gap-2 border border-[#E5E2DC] bg-white rounded-lg px-3.5 py-2.5 text-sm text-left focus:outline-none focus:ring-2 focus:ring-[#1E3F20] focus:border-transparent ${
          disabled ? "opacity-60 cursor-not-allowed" : "hover:border-[#1E3F20]/40"
        }`}
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        <span className={`truncate ${selected ? "text-[#1A2E1C]" : "text-[#8A8577]"}`}>
          {selected ? (
            <>
              {selected.label}
              {selected.sublabel && (
                <span className="text-[#5C6B5E]"> — {selected.sublabel}</span>
              )}
            </>
          ) : (
            placeholder
          )}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {allowClear && selected && !disabled && (
            <span
              role="button"
              aria-label="Clear"
              onClick={clear}
              className="text-[#8A8577] hover:text-red-600"
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className="w-4 h-4 text-[#5C6B5E]" />
        </span>
      </button>

      {open && (
        <div className="absolute z-40 mt-1 w-full bg-white border border-[#E5E2DC] rounded-lg shadow-lg overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E5E2DC] bg-[#F9F8F6]">
            <Search className="w-4 h-4 text-[#5C6B5E]" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="flex-1 bg-transparent text-sm focus:outline-none placeholder-[#8A8577]"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-xs text-[#8A8577] text-center">
                No matches
              </div>
            ) : (
              filtered.map((o) => {
                const isSel = String(o.value) === String(value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => pick(o.value)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-[#F1EFEA] flex items-start gap-2 ${
                      isSel ? "bg-[#1E3F20]/5" : ""
                    }`}
                    data-testid={testId ? `${testId}-opt-${o.value}` : undefined}
                  >
                    <span className="mt-0.5 shrink-0 w-4">
                      {isSel && <Check className="w-3.5 h-3.5 text-[#1E3F20]" />}
                    </span>
                    <span className="flex-1">
                      <span className="text-[#1A2E1C] font-medium">{o.label}</span>
                      {o.sublabel && (
                        <span className="block text-xs text-[#5C6B5E]">{o.sublabel}</span>
                      )}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchSelect;
